import { useEffect, useMemo, useState } from 'react'
import CustomerCombobox from '../CustomerCombobox'
import { supabase } from '../../api/supabase'

// Z93b: draft house (child) rows on the Create New Job page. Drafts live ONLY in React state --
// nothing is numbered or written until Save, which sends master + all rows to
// create_consol_with_children in one transaction. Refs shown here are previews: row order is the
// final sequence order (first row = -01) and the default text re-numbers live on reorder/delete.
// Staff may overwrite a row's ref; job_ref_index stays system-assigned server-side either way.

export interface DraftChild {
  key: string
  customRef: string | null // null = auto (default text follows row position)
  houseBlNo: string
  importerId: string
  consignorId: string
  containerNo: string
  pieces: string
  grossWeight: string
  volume: string
  goodsDescription: string
}

export const REF_FORMAT = /^[A-Z0-9-]{1,15}$/

export const newDraft = (): DraftChild => ({
  key: Math.random().toString(36).slice(2),
  customRef: null,
  houseBlNo: '', importerId: '', consignorId: '', containerNo: '',
  pieces: '', grossWeight: '', volume: '', goodsDescription: '',
})

export const defaultRef = (masterRef: string, position: number) =>
  `${masterRef || '<master>'}-${String(position + 1).padStart(2, '0')}`

export const effectiveRef = (masterRef: string, d: DraftChild, position: number) =>
  d.customRef ?? defaultRef(masterRef, position)

const inputCls = 'w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1.5 text-sm text-gray-900 dark:text-white'

// Format + in-draft duplicate problems. DB duplicates are looked up separately (async).
export function draftRefProblems(masterRef: string, drafts: DraftChild[]): Record<string, string> {
  const out: Record<string, string> = {}
  const seen = new Map<string, string>()
  drafts.forEach((d, i) => {
    const ref = effectiveRef(masterRef, d, i).toUpperCase()
    if (!REF_FORMAT.test(ref)) out[d.key] = 'Letters, digits and hyphens only, max 15 characters'
    else if (ref === masterRef.toUpperCase()) out[d.key] = 'Same as the master job reference'
    else if (seen.has(ref)) { out[d.key] = 'Duplicate of another row'; out[seen.get(ref)!] = 'Duplicate of another row' }
    else seen.set(ref, d.key)
  })
  return out
}

// Refs that already exist in the jobs table (debounced). Returns the set of taken refs.
export function useTakenRefs(refs: string[]): Set<string> {
  const [taken, setTaken] = useState<Set<string>>(new Set())
  const sig = refs.join('|')
  useEffect(() => {
    const list = refs.filter(r => REF_FORMAT.test(r))
    if (list.length === 0) { setTaken(new Set()); return }
    const t = setTimeout(async () => {
      const { data, error } = await supabase.from('jobs').select('job_ref').in('job_ref', list)
      if (!error) setTaken(new Set((data || []).map((r: { job_ref: string }) => r.job_ref.toUpperCase())))
    }, 400)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig])
  return taken
}

interface Props {
  masterRef: string
  transportMode: string
  drafts: DraftChild[]
  setDrafts: (d: DraftChild[]) => void
}

export default function ConsolDraftsTab({ masterRef, transportMode, drafts, setDrafts }: Props) {
  const isAir = transportMode === 'AIR'
  const localProblems = useMemo(() => draftRefProblems(masterRef, drafts), [masterRef, drafts])
  const refs = drafts.map((d, i) => effectiveRef(masterRef, d, i).toUpperCase())
  const taken = useTakenRefs(refs)

  const update = (key: string, patch: Partial<DraftChild>) =>
    setDrafts(drafts.map(d => (d.key === key ? { ...d, ...patch } : d)))
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= drafts.length) return
    const next = [...drafts]
    ;[next[i], next[j]] = [next[j], next[i]]
    setDrafts(next)
  }

  return (
    <div className="space-y-4">
      <div className="rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
        <b>This tab is for the house bills (HBL).</b> The master bill (MBL) details are in the other tabs.
        Direction, mode, ports, dates, vessel/voyage and carrier are copied from the master when you save — only fill what differs per shipment.
        Nothing is created or numbered until you press <b>Create Job</b>; you can also save with no house bills and add them later.
      </div>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">House bills ({drafts.length})</h3>
        <button type="button" onClick={() => setDrafts([...drafts, newDraft()])}
          className="px-3 py-1 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">+ Add house bill</button>
      </div>

      {drafts.length === 0 && (
        <div className="text-center py-8 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400">
          No house bills yet.
        </div>
      )}

      {drafts.map((d, i) => {
        const ref = refs[i]
        const problem = localProblems[d.key] || (taken.has(ref) ? `${ref} already exists` : null)
        return (
          <div key={d.key} className={`rounded-lg border p-3 ${problem ? 'border-red-400' : 'border-gray-200 dark:border-gray-700'}`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="text-xs text-gray-500">#{i + 1}</span>
              <input
                className={`${inputCls} !w-44 font-mono font-bold ${problem ? '!border-red-500 text-red-600' : d.customRef === null ? 'text-gray-400' : ''}`}
                value={effectiveRef(masterRef, d, i)}
                onChange={e => {
                  const v = e.target.value.toUpperCase()
                  update(d.key, { customRef: v === '' || v === defaultRef(masterRef, i) ? null : v })
                }}
                aria-label="Job reference"
              />
              <span className="text-xs text-gray-500">
                {d.customRef === null ? 'preview — assigned on save, follows row order' : 'custom reference'}
              </span>
              {d.customRef !== null && (
                <button type="button" className="text-xs underline text-gray-500" onClick={() => update(d.key, { customRef: null })}>use auto</button>
              )}
              <span className="ml-auto flex gap-1">
                <button type="button" className="px-2 py-1 text-xs border rounded disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                <button type="button" className="px-2 py-1 text-xs border rounded disabled:opacity-30" disabled={i === drafts.length - 1} onClick={() => move(i, 1)}>↓</button>
                <button type="button" className="px-2 py-1 text-xs border rounded text-red-600" onClick={() => setDrafts(drafts.filter(x => x.key !== d.key))}>Remove</button>
              </span>
            </div>
            {problem && <div className="mb-2 text-xs text-red-600">{problem}</div>}
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <label className="text-xs text-gray-600 dark:text-gray-400">{isAir ? 'House AWB' : 'House BL'}
                <input className={inputCls} value={d.houseBlNo} onChange={e => update(d.key, { houseBlNo: e.target.value })} />
              </label>
              <div className="text-xs text-gray-600 dark:text-gray-400">Consignor
                <CustomerCombobox value={d.consignorId} onChange={v => update(d.key, { consignorId: v })} anyRole placeholder="— Same as master —" />
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">Importer
                <CustomerCombobox value={d.importerId} onChange={v => update(d.key, { importerId: v })} anyRole placeholder="— Same as master —" />
              </div>
              <label className="text-xs text-gray-600 dark:text-gray-400">Container no.
                <input className={inputCls} value={d.containerNo} onChange={e => update(d.key, { containerNo: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-400">No. of pieces
                <input type="number" min="0" step="1" className={inputCls} value={d.pieces} onChange={e => update(d.key, { pieces: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-400">Gross weight (kg)
                <input type="number" min="0" step="any" className={inputCls} value={d.grossWeight} onChange={e => update(d.key, { grossWeight: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-400">Volume (m³)
                <input type="number" min="0" step="any" className={inputCls} value={d.volume} onChange={e => update(d.key, { volume: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600 dark:text-gray-400">Goods description
                <input className={inputCls} value={d.goodsDescription} onChange={e => update(d.key, { goodsDescription: e.target.value })} />
              </label>
            </div>
          </div>
        )
      })}
    </div>
  )
}
